// Загрузка брендинга компании (логотип / печать / подпись) в Supabase Storage.
// Путь: {companyId}/{kind}.{ext} в ПУБЛИЧНОМ bucket company-branding.
// Bucket публичный, поэтому getPublicUrl возвращает рабочую ссылку для
// страницы счёта/акта (её открывают клиенты без авторизации).
import { requireSupabase } from '../../domains/auth/supabaseClient';

const BRANDING_BUCKET = 'company-branding';

export type BrandingKind = 'logo' | 'stamp' | 'signature';

const MAX_BYTES = 2 * 1024 * 1024; // 2 МБ
const ALLOWED = ['image/png', 'image/jpeg', 'image/webp', 'image/svg+xml'];

function extFor(file: File): string {
  if (file.type === 'image/png') return 'png';
  if (file.type === 'image/jpeg') return 'jpg';
  if (file.type === 'image/webp') return 'webp';
  if (file.type === 'image/svg+xml') return 'svg';
  return 'png';
}

/**
 * Загружает изображение брендинга и возвращает публичный URL (с cache-buster,
 * чтобы после замены сразу показывалась новая картинка).
 */
export async function uploadCompanyBranding(
  companyId: string,
  kind: BrandingKind,
  file: File,
): Promise<string> {
  if (!ALLOWED.includes(file.type)) {
    throw new Error('Поддерживаются PNG, JPG, WEBP или SVG');
  }
  if (file.size > MAX_BYTES) {
    throw new Error('Файл слишком большой (максимум 2 МБ)');
  }

  const supabase = requireSupabase();
  const path = `${companyId}/${kind}.${extFor(file)}`;

  const { error } = await supabase.storage.from(BRANDING_BUCKET).upload(path, file, {
    upsert: true,
    contentType: file.type,
  });
  if (error) throw new Error(`Не удалось загрузить файл: ${error.message}`);

  const { data } = supabase.storage.from(BRANDING_BUCKET).getPublicUrl(path);
  // cache-buster, иначе браузер/CDN покажут старую версию при замене
  return `${data.publicUrl}?v=${Date.now()}`;
}

/** Удаляет файл брендинга (всех известных расширений) для kind. */
export async function deleteCompanyBranding(companyId: string, kind: BrandingKind): Promise<void> {
  const supabase = requireSupabase();
  const paths = ['png', 'jpg', 'webp', 'svg'].map((e) => `${companyId}/${kind}.${e}`);
  await supabase.storage.from(BRANDING_BUCKET).remove(paths).catch(() => null);
}
